import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  FINCHGPT_EVENTS,
  FINCHGPT_PROPERTIES,
  getWindowDimensions,
} from '@/constants/analytics/finchgpt-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';

import { Button } from '../../ui/button';
import { useAgentChatCompound } from '../AgentChat';
import { useAgentChat } from '../context/AgentChatContext';
import { SidebarRail } from './SidebarRail';

interface ResponsiveLayoutProps {
  sidebar: React.ReactNode;
  content: React.ReactNode;
}

// Mobile animation variants
const mobileSlideVariants = {
  list: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  },
  chat: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  },
};

// Constants
const COLLAPSE_THRESHOLD = 80; // pixels
const RAIL_WIDTH = 40; // pixels
const DEFAULT_SIDEBAR_WIDTH = 200; // pixels - compact 1:4 ratio with content
const MIN_SIDEBAR_WIDTH = 160;
const MAX_SIDEBAR_WIDTH_PERCENT = 35;

export const ResponsiveLayout = ({
  sidebar,
  content,
}: ResponsiveLayoutProps) => {
  const capture = usePostHogCapture();
  const {
    isSidebarOpen,
    setIsSidebarOpen,
    mobileView,
    setMobileView,
    selectedThreadId,
  } = useAgentChat();

  const { threads, handleCreateThreadDirect, matterId } = useAgentChatCompound();

  const previousWidth = useRef(window.innerWidth);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sidebar width state (pixels during drag, then stored)
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const [isNearCollapse, setIsNearCollapse] = useState(false);
  const [currentDragWidth, setCurrentDragWidth] = useState<number | null>(null);

  // Ref to track drag width for mouseup handler (avoids stale closure)
  const dragWidthRef = useRef<number | null>(null);

  // Toggle sidebar
  const toggleSidebar = useCallback(() => {
    const newState = !isSidebarOpen;
    setIsSidebarOpen(newState);
    setIsNearCollapse(false);

    capture(FINCHGPT_EVENTS.SIDEBAR_TOGGLED, {
      [FINCHGPT_PROPERTIES.MATTER_ID]: matterId,
      [FINCHGPT_PROPERTIES.IS_SIDEBAR_OPEN]: newState,
      [FINCHGPT_PROPERTIES.SIDEBAR_WIDTH]: sidebarWidth,
      ...getWindowDimensions(),
    });
  }, [isSidebarOpen, setIsSidebarOpen, capture, matterId, sidebarWidth]);

  // Handle drag start from resize handle
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const startX = e.clientX;
      const startWidth = sidebarWidth;
      const containerWidth =
        containerRef.current?.offsetWidth || window.innerWidth;
      const maxWidth = containerWidth * (MAX_SIDEBAR_WIDTH_PERCENT / 100);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const newWidth = Math.max(
          RAIL_WIDTH,
          Math.min(maxWidth, startWidth + deltaX),
        );

        dragWidthRef.current = newWidth;
        setCurrentDragWidth(newWidth);
        setIsNearCollapse(newWidth < COLLAPSE_THRESHOLD);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Use ref value to avoid stale closure
        const finalWidth = dragWidthRef.current ?? startWidth;
        if (finalWidth < COLLAPSE_THRESHOLD) {
          setIsSidebarOpen(false);
        } else {
          setSidebarWidth(Math.max(MIN_SIDEBAR_WIDTH, finalWidth));
        }
        dragWidthRef.current = null;
        setCurrentDragWidth(null);
        setIsNearCollapse(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [sidebarWidth, setIsSidebarOpen],
  );

  // Handle drag from collapsed rail to expand
  const handleExpandDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const containerWidth =
        containerRef.current?.offsetWidth || window.innerWidth;
      const maxWidth = containerWidth * (MAX_SIDEBAR_WIDTH_PERCENT / 100);

      // Set initial width to current mouse position immediately
      const initialWidth = Math.max(RAIL_WIDTH, Math.min(maxWidth, e.clientX));
      dragWidthRef.current = initialWidth;
      setCurrentDragWidth(initialWidth);
      setIsNearCollapse(initialWidth < COLLAPSE_THRESHOLD);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const newWidth = Math.max(
          RAIL_WIDTH,
          Math.min(maxWidth, moveEvent.clientX),
        );

        dragWidthRef.current = newWidth;
        setCurrentDragWidth(newWidth);
        setIsNearCollapse(newWidth < COLLAPSE_THRESHOLD);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Use ref value to avoid stale closure
        const finalWidth = dragWidthRef.current ?? RAIL_WIDTH;
        if (finalWidth < COLLAPSE_THRESHOLD) {
          setIsSidebarOpen(false);
        } else {
          setIsSidebarOpen(true);
          setSidebarWidth(Math.max(MIN_SIDEBAR_WIDTH, finalWidth));
        }
        dragWidthRef.current = null;
        setCurrentDragWidth(null);
        setIsNearCollapse(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [setIsSidebarOpen],
  );

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const isMobile = currentWidth < 768;
      const wasMobile = previousWidth.current < 768;

      if (isMobile && !wasMobile) {
        setIsSidebarOpen(false);
      }

      if (!isMobile && wasMobile) {
        setIsSidebarOpen(true);
      }

      previousWidth.current = currentWidth;
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsSidebarOpen]);

  // Switch to chat view when a thread is selected on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      if (selectedThreadId) {
        setMobileView('chat');
      } else {
        setMobileView('list');
      }
    }
  }, [selectedThreadId, setMobileView]);

  // Calculate the current width to display
  const displayWidth =
    isDragging && currentDragWidth !== null
      ? currentDragWidth
      : isSidebarOpen
        ? sidebarWidth
        : RAIL_WIDTH;

  // Mobile view
  const renderMobileView = () => {
    return (
      <div className="flex flex-col h-full w-full md:hidden">
        <AnimatePresence mode="wait">
          {mobileView === 'list' ? (
            <motion.div
              key="list"
              variants={mobileSlideVariants.list}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="h-full"
            >
              {sidebar}
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              variants={mobileSlideVariants.chat}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="h-full flex flex-col"
            >
              <div className="border-b px-4 py-2 flex items-center bg-white/80 backdrop-blur-sm">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileView('list')}
                  className="mr-2 hover:bg-slate-100"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-medium text-sm">Back to Threads</span>
              </div>
              <div className="flex-1 overflow-hidden">{content}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Desktop view with premium drag interaction
  const renderDesktopView = () => {
    return (
      <div ref={containerRef} className="hidden md:flex h-full w-full">
        {/* Sidebar container - follows mouse during drag, spring animation on release */}
        <motion.div
          className="h-full flex-shrink-0 relative overflow-hidden"
          animate={{ width: displayWidth }}
          transition={
            isDragging
              ? { duration: 0 } // Instant during drag
              : { type: 'spring', stiffness: 400, damping: 35 } // Spring on release
          }
        >
          {/* Rail overlay - fades in when near collapse or collapsed (but not when dragging above threshold) */}
          <motion.div
            className="absolute inset-0 z-10"
            initial={false}
            animate={{
              opacity:
                isNearCollapse || (!isSidebarOpen && !isDragging) ? 1 : 0,
              pointerEvents:
                isNearCollapse || (!isSidebarOpen && !isDragging)
                  ? 'auto'
                  : 'none',
            }}
            transition={{ duration: 0.15 }}
          >
            <SidebarRail
              threadCount={threads.length}
              onExpand={toggleSidebar}
              onCreateThread={handleCreateThreadDirect}
              onDragStart={handleExpandDrag}
            />
          </motion.div>

          {/* Sidebar content - dims when near collapse, shows during drag */}
          <motion.div
            className="h-full w-full flex"
            initial={false}
            animate={{
              opacity: isNearCollapse
                ? 0.3
                : isSidebarOpen || isDragging
                  ? 1
                  : 0,
            }}
            transition={{ duration: 0.15 }}
          >
            <div className="flex-1 h-full overflow-hidden">{sidebar}</div>
            {/* Resize handle */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className={cn(
                'w-4 h-full cursor-col-resize flex-shrink-0',
                'flex items-center justify-center',
                'group',
              )}
              onMouseDown={handleDragStart}
            >
              <motion.div
                className={cn(
                  'w-1 h-full rounded-full',
                  'bg-slate-200',
                  'group-hover:bg-slate-400',
                )}
                animate={{
                  backgroundColor: isDragging
                    ? 'rgb(148, 163, 184)'
                    : undefined,
                }}
                transition={{ duration: 0.15 }}
              />
            </div>
          </motion.div>
        </motion.div>

        {/* Content area - always stable, never unmounts */}
        <div className="flex-1 h-full min-w-0">{content}</div>
      </div>
    );
  };

  return (
    <>
      {renderMobileView()}
      {renderDesktopView()}
    </>
  );
};
